import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import { MailCheck, Send, ShieldCheck, Clock, Eye } from "lucide-react";

const C = { navy: "#1C4358", slate: "#4B5563", charcoal: "#252221", line: "#E6EDF2", green: "#2F7D5A", amber: "#B7791F" };
const TZS = ["America/Chicago", "America/New_York", "America/Denver", "America/Los_Angeles", "America/Toronto", "UTC"];

export default function SecurityDigest() {
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = () => api.get("/security/digest-settings").then(({ data }) => setS(data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try { const { data } = await api.put("/security/digest-settings", patch); setS(data); toast.success("Saved"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try { const { data } = await api.get("/security/digest/preview"); setPreview(data); }
    catch (e) { toast.error(errMsg(e)); setPreviewOpen(false); }
    finally { setPreviewLoading(false); }
  };

  const toggleRecipient = (id) => {
    const cur = new Set(s.recipient_user_ids || []);
    cur.has(id) ? cur.delete(id) : cur.add(id);
    save({ recipient_user_ids: [...cur] });
  };

  const sendTest = async () => {
    try { const { data } = await api.post("/security/digest/send-test", {}); toast.success(data.message); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (loading) return <div className="max-w-2xl mx-auto"><Skeleton className="h-64" /></div>;
  if (!s) return null;

  return (
    <div className="max-w-2xl mx-auto pb-16" data-testid="security-digest-page">
      <button onClick={() => navigate("/security")} className="text-[12.5px] mb-2 inline-flex items-center gap-1" style={{ color: C.slate }} data-testid="digest-back">
        <ShieldCheck size={13} /> Security &amp; Compliance
      </button>
      <h1 className="font-display text-2xl font-bold flex items-center gap-2 mb-1" style={{ color: C.charcoal }}>
        <MailCheck size={22} style={{ color: C.navy }} /> Weekly Security Digest
      </h1>
      <p className="text-[13px] mb-5" style={{ color: C.slate }}>
        Email an aggregate-only weekly summary of security &amp; compliance signals to your team. Internal recipients only — never clients, partners, or guests.
        <span className="ml-1" style={{ color: s.scope === "global" ? C.green : C.amber }}>Scope: {s.scope === "global" ? "Global" : "Your branches"}.</span>
      </p>

      <Card className="p-4 border-[#E6EDF2] mb-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[14px] font-semibold" style={{ color: C.charcoal }}>Weekly auto-send</div>
            <div className="text-[12px]" style={{ color: C.slate }}>Sends automatically on the schedule below.</div>
          </div>
          <Switch checked={!!s.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="digest-enabled" />
        </div>
      </Card>

      <Card className="p-4 border-[#E6EDF2] mb-4">
        <div className="text-[13px] font-semibold mb-3" style={{ color: C.charcoal }}>Schedule</div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-[12px]">Day</Label>
            <Select value={String(s.day_of_week)} onValueChange={(v) => save({ day_of_week: Number(v) })}>
              <SelectTrigger className="mt-1 h-9" data-testid="digest-day"><SelectValue /></SelectTrigger>
              <SelectContent>{(s.day_names || []).map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[12px]">Time</Label>
            <Input type="time" value={s.time_of_day} onChange={(e) => setS({ ...s, time_of_day: e.target.value })}
              onBlur={(e) => save({ time_of_day: e.target.value })} className="mt-1 h-9" data-testid="digest-time" />
          </div>
          <div>
            <Label className="text-[12px]">Timezone</Label>
            <Select value={s.timezone} onValueChange={(v) => save({ timezone: v })}>
              <SelectTrigger className="mt-1 h-9" data-testid="digest-tz"><SelectValue /></SelectTrigger>
              <SelectContent>{TZS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="text-[12px] mt-3 flex items-center gap-1.5" style={{ color: C.slate }} data-testid="digest-next-run">
          <Clock size={13} /> Next run: {s.next_run_preview ? formatDateTime(s.next_run_preview) : "—"}
          {s.last_queued_at && <span className="ml-2">· Last queued: {formatDateTime(s.last_queued_at)}</span>}
        </div>
      </Card>

      <Card className="p-4 border-[#E6EDF2] mb-4">
        <div className="text-[13px] font-semibold mb-1" style={{ color: C.charcoal }}>Recipients</div>
        <div className="text-[12px] mb-3" style={{ color: C.slate }}>Internal admins/managers within your scope only.</div>
        <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="digest-recipients">
          {(s.eligible_recipients || []).length === 0 ? (
            <div className="text-[12.5px]" style={{ color: C.slate }}>No eligible internal recipients in your scope.</div>
          ) : (s.eligible_recipients || []).map((r) => (
            <label key={r.id} className="flex items-center gap-2.5 cursor-pointer" data-testid={`digest-recipient-${r.id}`}>
              <Checkbox checked={(s.recipient_user_ids || []).includes(r.id)} onCheckedChange={() => toggleRecipient(r.id)} />
              <span className="text-[13px]" style={{ color: C.charcoal }}>{r.name}</span>
              <span className="text-[11px]" style={{ color: C.slate }}>{r.email} · {String(r.role).replace(/_/g, " ")}</span>
            </label>
          ))}
        </div>
      </Card>

      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={sendTest} disabled={saving} className="gap-1.5 bg-[#1C4358]" data-testid="digest-send-test">
          <Send size={15} /> Send test digest now
        </Button>
        <Button onClick={openPreview} variant="outline" className="gap-1.5 border-[#1C4358] text-[#1C4358]" data-testid="digest-preview-btn">
          <Eye size={15} /> Preview last week&apos;s digest
        </Button>
        <span className="text-[12px]" style={{ color: C.slate }}>Preview is read-only — no email is sent.</span>
      </div>
      <p className="text-[11px] mt-4" style={{ color: C.slate }}>Internal security digest — aggregate data only. No client names, contact details, tokens, or private content are ever included.</p>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden" data-testid="digest-preview-modal">
          <DialogHeader className="px-5 pt-5 pb-3">
            <DialogTitle className="text-[15px] flex items-center gap-2" style={{ color: C.charcoal }}>
              <Eye size={16} style={{ color: C.navy }} /> Digest preview
              <span className="ml-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FBF7EC", color: C.amber, border: "1px solid #EADFBF" }} data-testid="digest-preview-badge">
                Preview only — no email sent
              </span>
            </DialogTitle>
            <DialogDescription className="sr-only">Read-only preview of this week&apos;s aggregate security digest. No email is sent.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[70vh] overflow-y-auto px-5 pb-5">
            {previewLoading || !preview ? (
              <Skeleton className="h-64" data-testid="digest-preview-loading" />
            ) : (
              <div data-testid="digest-preview-content">
                <div className="rounded-t-lg px-4 py-3" style={{ background: C.navy, color: "#fff" }}>
                  <div className="text-[10px] tracking-wide uppercase opacity-80">MovingMyMama · Command Center</div>
                  <div className="text-[16px] font-bold">Weekly Security Digest</div>
                  <div className="text-[11.5px] opacity-90">{preview.date_from} → {preview.date_to} · {preview.scope_label}</div>
                </div>
                <div className="border border-t-0 rounded-b-lg p-4" style={{ borderColor: C.line }}>
                  <div className="rounded-lg px-3 py-2 mb-3" style={{ background: "#FBF7EC", border: "1px solid #EADFBF" }}>
                    <div className="text-[12.5px] font-semibold" style={{ color: C.amber }}>Top items needing review</div>
                    {(preview.top_items || []).length === 0 ? (
                      <div className="text-[12.5px] mt-0.5" style={{ color: C.green }}>Nothing needs immediate attention this week.</div>
                    ) : (
                      <ul className="mt-1 pl-4 list-disc text-[12.5px]" style={{ color: "#8a6d1f" }} data-testid="digest-preview-top">
                        {preview.top_items.map((t, i) => <li key={i}>{t}</li>)}
                      </ul>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2" data-testid="digest-preview-cards">
                    {(preview.cards || []).map((c) => (
                      <div key={c.key} className="rounded-lg p-2.5 border" style={{ borderColor: C.line }} data-testid={`digest-preview-card-${c.key}`}>
                        <div className="text-[10.5px]" style={{ color: C.slate }}>{c.label}</div>
                        <div className="text-[20px] font-bold" style={{ color: C.navy }}>{c.value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 text-[12.5px]" data-testid="digest-preview-links">
                    {(preview.links || []).map((l, i) => (
                      <span key={l.path}>
                        <span style={{ color: l.primary ? "#9C1E22" : C.navy, fontWeight: l.primary ? 600 : 400 }}>{l.label}</span>
                        {i < preview.links.length - 1 && <span style={{ color: C.slate }}> · </span>}
                      </span>
                    ))}
                    <div className="text-[10.5px] mt-1" style={{ color: C.slate }}>(Links open inside the emailed digest.)</div>
                  </div>
                  <p className="text-[10.5px] mt-4 pt-2.5 border-t" style={{ color: C.slate, borderColor: C.line }} data-testid="digest-preview-disclaimer">
                    {preview.disclaimer}
                  </p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
